import { FormApi } from "final-form";
import React, { useCallback, useState } from "react";
import { useActionRequest, useDataRequest } from "../../../../common/hooks/api";
import { NetworkRequestState } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import { PrivacyCenterDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import {
  OrganizationPublicId,
  PrivacyCenterImageType,
} from "../../../../common/service/server/Types";
import { Error as OrganizationError } from "../../../copy/organization";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../hooks";
import { usePrivacyCenter } from "../../../hooks/usePrivacyCenter";
import { PrivacyCenterResidencySettingsRow } from "../../organisms/settings/rows/PrivacyCenterResidencySettingsRow";
import { PrivacyCenterDpoRow } from "../../organisms/settings/rows/PrivacyCenterDpoRow";
import {
  CustomFieldEnabledType,
  PrivacyCenterRequestFormSettingsRow,
} from "../../organisms/settings/rows/PrivacyCenterRequestFormSettingsRow";
import { PrivacyCenterUrlSettingRow } from "../../organisms/settings/rows/PrivacyCenterUrlSettingsRow";
import { PrivacyNoticeDto } from "../../../../common/service/server/controller/PrivacyNoticeController";
import { OrganizationDto } from "../../../../common/service/server/dto/OrganizationDto";
import { PrivacyCenterPipedaPrivacyOfficerRow } from "../../organisms/settings/rows/PrivacyCenterPipedaPrivacyOfficerRow";
import { PrivacyCenterOptOutSettingsRow } from "../../organisms/settings/rows/PrivacyCenterOptOutSettingsRow";
import { PrivacyCenterOptOutNoticeEditorRow } from "../../organisms/settings/rows/PrivacyCenterOptOutNoticeEditorRow";
import { privacyCenterSettingsUpdate } from "../../../util/privacyCenterUtil";

export type PrivacyCenterSettingsProps = {
  organization: OrganizationDto;
  organizationId: OrganizationPublicId;
  privacyCenterUrl: string;
  logoUrl: string;
  logoLinkUrl: string;
  dpoName: string | undefined;
  dpoEmail: string | undefined;
  dpoPhone: string | undefined;
  dpoAddress: string | undefined;
  dpoDoesNotApply: boolean;
  eeaContactName: string | undefined;
  eeaContactAddress: string | undefined;
  eeaContactEmail: string | undefined;
  eeaContactPhone: string | undefined;
  eeaContactDoesNotApply: boolean;
  pipedaOfficerName: string | undefined;
  pipedaOfficerEmail: string | undefined;
  pipedaOfficerPhone: string | undefined;
  pipedaOfficerAddress: string | undefined;
  requiresDpo: boolean;
  hasDpoTask: boolean;
  faviconUrl: string;
  disableCaResidencyConfirmation: boolean;
  hideLocationDropdownOnRequestForm: boolean;
  addOtherOptionToLocationDropdownOnRequestForm: boolean;
  customFieldEnabled: boolean;
  customFieldHelp: string;
  customFieldLabel: string;
  handleFormSubmit: (values, form?: FormApi<any>) => Promise<void>;
  handleDeleteImage: (imageType: PrivacyCenterImageType) => Promise<void>;
  handleDoItLaterTask: () => Promise<void>;
  handleDpoDoesNotApply: (doesNotApply: boolean) => Promise<void>;
  optOutConfirmationMessage: string;
  includeUrlField?: boolean;
  includeRequestFormField?: boolean;
  includeOptOutFormField?: boolean;
  includeOptOutNoticeEditor?: boolean;
  customOptOutFieldEnabled: boolean;
  customOptOutFieldHelp: string;
  customOptOutFieldLabel: string;
  includeDpoField?: boolean;
  includePIPEDADpoField?: boolean;
  includeResidencyFields?: boolean;
  topDivider?: boolean;
  optOut: PrivacyNoticeDto;
  showExtendedAddressFormForOptOut?: boolean;
  showExtendedAddressFormForRequests?: boolean;
  showPhoneNumberFormForOptOut?: boolean;
  showPhoneNumberFormForRequests?: boolean;
  requireFirstLastNameOnYpcPage?: boolean;
  includeOptOutConfirmationField?: boolean;
};
export const PrivacyCenterSettings: React.FC<PrivacyCenterSettingsProps> = ({
  organization,
  privacyCenterUrl,
  optOut,
  dpoName,
  dpoEmail,
  dpoPhone,
  dpoAddress,
  requiresDpo,
  hasDpoTask,
  hideLocationDropdownOnRequestForm,
  addOtherOptionToLocationDropdownOnRequestForm,
  customFieldEnabled,
  customFieldHelp,
  customFieldLabel,
  handleFormSubmit,
  handleDoItLaterTask,
  optOutConfirmationMessage,
  includeUrlField = false,
  includeRequestFormField = false,
  includeOptOutFormField = false,
  includeOptOutNoticeEditor = false,
  customOptOutFieldEnabled,
  customOptOutFieldHelp,
  customOptOutFieldLabel,
  includeDpoField = true,
  includePIPEDADpoField = true,
  pipedaOfficerName,
  pipedaOfficerEmail,
  pipedaOfficerPhone,
  pipedaOfficerAddress,
  includeResidencyFields = true,
  topDivider = false,
  showExtendedAddressFormForOptOut = false,
  showExtendedAddressFormForRequests = false,
  showPhoneNumberFormForOptOut = false,
  showPhoneNumberFormForRequests = false,
  requireFirstLastNameOnYpcPage = false,
  includeOptOutConfirmationField = false,
}) => {
  const [hasRequestInFlight, setHasRequestInFlight] = useState<boolean>(false);

  // Before it hits the backend, have the request handler indicate that we're submitting the form so other forms on this
  // page can switch to a loading state. Ideally we'd do this through NetworkRequestState, but we'd have to plumb it all
  // the way from the call to `usePrivacyCenter` a few parent components up.
  const wrapRequestHandler = useCallback((handler) => {
    return async (...args) => {
      setHasRequestInFlight(true);
      try {
        await handler(...args);
      } finally {
        // This is a workaround to a more fundamental issue with the way privacy center updates are done. Essentially we
        // do a PUT to save the value and in the `onSuccess` of the API layer, and there is an immediate `GET` to update
        // local form values to reflect the new state. On slow enough connections, the PUT finishes, releasing the await
        // of the update promise, but the GET comes later. So, if *another* state change is requested in that window, it
        // can wind up "resetting" other form values that were just changed.
        //
        // A proper fix involves refactoring the `onSuccess => fetch` structure of the `usePrivacyCenterSettings` API to
        // remove reliance on the backend to tell us the updated form state to use. But that's a larger renovation.
        //
        // For this change, just try throttling to 2G speed and clicking as fast as you can on the checkbox with/without
        // this, you'll see what I mean :) overall this sleep solves POL-5162.
        setTimeout(() => {
          setHasRequestInFlight(false);
        }, 1000);
      }
    };
  }, []);
  return (
    <div className="max-width-896">
      {topDivider && <hr className="mb-lg" />}
      {includeUrlField && (
        <>
          <PrivacyCenterUrlSettingRow url={privacyCenterUrl} />
          <hr className="my-lg" />
        </>
      )}

      {includeRequestFormField && (
        <>
          <PrivacyCenterRequestFormSettingsRow
            customFieldEnabled={customFieldEnabled}
            customFieldHelp={customFieldHelp}
            customFieldLabel={customFieldLabel}
            customFieldEnabledType={CustomFieldEnabledType.REQUEST_FORM}
            handleFormSubmit={wrapRequestHandler(handleFormSubmit)}
            extendedAddressFormValue={showExtendedAddressFormForRequests}
            extendedAddressFormKey="showExtendedAddressFormForRequests"
            showExtendedAddressForm={true}
            phoneNumberFormValue={showPhoneNumberFormForRequests}
            phoneNumberFormKey="showPhoneNumberFormForRequests"
            showPhoneNumberForm={true}
            firstLastNameRequiredValue={requireFirstLastNameOnYpcPage}
            firstLastNameRequiredKey="requireFirstLastNameOnYpcPage"
            showFirstLastNameRequiredField={true}
            showCustomField={true}
            otherFormsSubmitting={hasRequestInFlight}
          />
        </>
      )}

      {includeOptOutFormField && (
        <>
          <hr className="my-lg" />
          <PrivacyCenterOptOutSettingsRow
            customFieldEnabled={customOptOutFieldEnabled}
            customFieldHelp={customOptOutFieldHelp}
            customFieldLabel={customOptOutFieldLabel}
            customFieldEnabledType={CustomFieldEnabledType.OPT_OUT}
            optOutConfirmationMessage={optOutConfirmationMessage}
            handleFormSubmit={wrapRequestHandler(handleFormSubmit)}
            showOptOutConfirmationMessageField={includeOptOutConfirmationField}
            extendedAddressFormValue={showExtendedAddressFormForOptOut}
            extendedAddressFormKey="showExtendedAddressFormForOptOut"
            showExtendedAddressForm={true}
            phoneNumberFormValue={showPhoneNumberFormForOptOut}
            phoneNumberFormKey="showPhoneNumberFormForOptOut"
            showPhoneNumberForm={true}
            showCustomField={true}
            otherFormsSubmitting={hasRequestInFlight}
          />
        </>
      )}

      {includeOptOutNoticeEditor && (
        <>
          <hr className="my-lg" />
          <PrivacyCenterOptOutNoticeEditorRow label={optOut.label} notice={optOut.notice} />
        </>
      )}
      {includeResidencyFields && (
        <>
          <hr className="my-lg" />
          <PrivacyCenterResidencySettingsRow
            residencySettingsFields={{
              hideLocationDropdownOnRequestForm,
              addOtherOptionToLocationDropdownOnRequestForm,
            }}
            handleFormSubmit={handleFormSubmit}
          />
        </>
      )}

      {includeDpoField && (
        <>
          <hr className="my-lg" />
          <PrivacyCenterDpoRow
            title={
              requiresDpo
                ? "Data Protection Officer (required)"
                : "Data Protection Officer (optional)"
            }
            dpoName={dpoName}
            dpoEmail={dpoEmail}
            dpoPhone={dpoPhone}
            dpoAddress={dpoAddress}
            requiresDpo={requiresDpo}
            hasDpoTask={hasDpoTask}
            handleFormSubmit={handleFormSubmit}
            handleAddTask={handleDoItLaterTask}
            hideFooter
          />
        </>
      )}

      {includePIPEDADpoField && (
        <>
          <hr className="my-lg" />
          <PrivacyCenterPipedaPrivacyOfficerRow
            title={"Canadian Data Privacy Officer (required)"}
            helpText={
              "Canadian privacy laws require your organization to name a privacy officer in your online privacy policy. Your privacy officer is a person either inside your organization or external to it (such as a Data Privacy Officer (DPO) who is accountable for PIPEDA compliance. You can put the name of a member of your privacy team and your new privacy@yourdomain email address as the contact email."
            }
            organization={organization}
            dpoName={dpoName}
            dpoEmail={dpoEmail}
            dpoPhone={dpoPhone}
            dpoAddress={dpoAddress}
            pipedaOfficerName={pipedaOfficerName}
            pipedaOfficerEmail={pipedaOfficerEmail}
            pipedaOfficerPhone={pipedaOfficerPhone}
            pipedaOfficerAddress={pipedaOfficerAddress}
            handleFormSubmit={handleFormSubmit}
          />
        </>
      )}
    </div>
  );
};

const OPT_OUT_CONFIRMATION_MESSAGE_SLUG = "closed-opt-out";

export const usePrivacyCenterSettings = (options?: {
  enabled?: boolean;
}): UsePrivacyCenterSettings => {
  const enabled = options?.enabled ?? true;
  const notReady = !enabled;
  const organizationId = usePrimaryOrganizationId();

  const [organization, orgRequest] = usePrimaryOrganization();
  const [privacyCenter, privacyCentersRequest, fetch] = usePrivacyCenter(organizationId, notReady);
  const { result: noticeDto } = useDataRequest({
    queryKey: ["notice", "opt-out", organizationId],
    api: () => Api.privacyNotice.getOptOutPrivacyNotice(organizationId),
    notReady,
  });
  const { result: getOrgMsgSampleResult } = useDataRequest({
    queryKey: ["emailTemplate", organizationId, ""],
    api: () =>
      Api.organization.getOrganizationMessageSample(
        organizationId,
        OPT_OUT_CONFIRMATION_MESSAGE_SLUG,
      ),
    notReady,
  });
  const optOutConfirmationMessage = getOrgMsgSampleResult?.customText ?? "";

  const updatePrivacyCenterApi = useCallback(
    (submitValues) =>
      Api.privacyCenter.updatePrivacyCenter(organization.id, privacyCenter.id, submitValues),
    [organization?.id, privacyCenter?.id],
  );

  const deletePrivacyCenterImageApi = useCallback(
    (imageType: PrivacyCenterImageType) =>
      Api.privacyCenter.deletePrivacyCenterImage(organization.id, privacyCenter.id, imageType),
    [organization?.id, privacyCenter?.id],
  );

  const { fetch: updatePrivacyCenterValues } = useActionRequest({
    api: updatePrivacyCenterApi,
    messages: {
      forceError: OrganizationError.UpdateOrganization,
    },
    onSuccess: () => fetch(),
  });

  const { fetch: deletePrivacyCenterImage } = useActionRequest({
    api: deletePrivacyCenterImageApi,
    messages: {
      forceError: OrganizationError.DeletePrivacyCenterImage,
    },
    onSuccess: () => fetch(),
  });

  const { fetch: updateOptOutConfirmationText } = useActionRequest({
    api: (customMessage) =>
      Api.organization.updateOrganizationMessageSample(
        organization.id,
        OPT_OUT_CONFIRMATION_MESSAGE_SLUG,
        customMessage?.trim() ?? "",
      ),
  });

  const handleFormSubmit = useCallback(
    async (values, form?: FormApi<any>) => {
      if (CustomFieldEnabledType.REQUEST_FORM in values) {
        const { REQUEST_FORM, ...coreValues } = values;
        const updatedValues = {
          ...coreValues,
          customFieldEnabled: REQUEST_FORM,
        };
        await Api.organization.updateCustomField(organization.id, updatedValues);
        fetch();
        form?.reset(values);
        return;
      }

      if (CustomFieldEnabledType.OPT_OUT in values) {
        const { OPT_OUT, ...coreValues } = values;
        const updatedValues = {
          ...coreValues,
          customFieldEnabled: OPT_OUT,
        };
        await Api.organization.updateCustomOptOutField(organization.id, updatedValues);
        fetch();
        form?.reset(values);
        return;
      }

      if (CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE in values) {
        let message = values.optOutConfirmationMessage;
        if (values[CustomFieldEnabledType.OPT_OUT_CONFIRMATION_MESSAGE] == false) {
          message = "";
        }
        await updateOptOutConfirmationText(message);
        form?.reset(values);
        return;
      }

      // Handle regular privacy center updates
      await updatePrivacyCenterValues(privacyCenterSettingsUpdate(privacyCenter, values));
    },
    [
      organization?.id,
      updateOptOutConfirmationText,
      updatePrivacyCenterValues,
      privacyCenter,
      fetch,
    ],
  );

  const handleDeleteImage = useCallback(
    (imageType) => deletePrivacyCenterImage(imageType),
    [deletePrivacyCenterImage, privacyCenter],
  );

  const handleDoItLaterTask = async () => {
    await Api.tasks.createAddDpoTask(organization.id);
    fetch();
  };

  const updatePolicyApi = useCallback(
    async (values) => {
      await Api.privacyCenter.updatePrivacyCenter(organizationId, privacyCenter?.id, values);
    },
    [organizationId, privacyCenter?.id],
  );

  const { fetch: updatePrivacyPolicy, request: updatePrivacyPolicyRequest } = useActionRequest({
    api: updatePolicyApi,
    onSuccess: () => fetch(),
  });

  const handleDpoDoesNotApply = useCallback(
    (doesNotApply: boolean) =>
      updatePrivacyPolicy(
        privacyCenterSettingsUpdate(privacyCenter, { dpoOfficerDoesNotApply: doesNotApply }),
      ),
    [privacyCenter, updatePrivacyPolicy],
  );

  return {
    settings: {
      organization,
      organizationId,
      privacyCenterUrl: privacyCenter?.url,
      logoUrl: privacyCenter?.logoUrl,
      dpoName: privacyCenter?.dpoOfficerName,
      dpoEmail: privacyCenter?.dpoOfficerEmail,
      dpoPhone: privacyCenter?.dpoOfficerPhone,
      dpoAddress: privacyCenter?.dpoOfficerAddress,
      eeaContactName: privacyCenter?.eeaContactName,
      eeaContactAddress: privacyCenter?.eeaContactAddress,
      eeaContactEmail: privacyCenter?.eeaContactEmail,
      eeaContactPhone: privacyCenter?.eeaContactPhone,
      eeaContactDoesNotApply: privacyCenter?.eeaContactDoesNotApply,
      pipedaOfficerName: privacyCenter?.pipedaOfficerName,
      pipedaOfficerEmail: privacyCenter?.pipedaOfficerEmail,
      pipedaOfficerPhone: privacyCenter?.pipedaOfficerPhone,
      pipedaOfficerAddress: privacyCenter?.pipedaOfficerAddress,
      requiresDpo: privacyCenter?.requiresDpo ?? false,
      hasDpoTask: privacyCenter?.hasDpoTask ?? false,
      dpoDoesNotApply: privacyCenter?.dpoOfficerDoesNotApply,
      optOut: noticeDto,
      logoLinkUrl: privacyCenter?.logoLinkUrl,
      faviconUrl: privacyCenter?.faviconUrl,
      disableCaResidencyConfirmation: privacyCenter?.disableCaResidencyConfirmation,
      hideLocationDropdownOnRequestForm: privacyCenter?.hideLocationDropdownOnRequestForm,
      addOtherOptionToLocationDropdownOnRequestForm:
        privacyCenter?.addOtherOptionToLocationDropdownOnRequestForm,
      customFieldEnabled: organization.customFieldEnabled,
      customFieldHelp: organization.customFieldHelp,
      customFieldLabel: organization.customFieldLabel,
      customOptOutFieldEnabled: organization.customOptOutFieldEnabled,
      customOptOutFieldHelp: organization.customOptOutFieldHelp,
      customOptOutFieldLabel: organization.customOptOutFieldLabel,
      handleFormSubmit: handleFormSubmit,
      handleDeleteImage: handleDeleteImage,
      handleDoItLaterTask: handleDoItLaterTask,
      handleDpoDoesNotApply: handleDpoDoesNotApply,
      optOutConfirmationMessage: optOutConfirmationMessage,
      includeDpoField: organization.featureGdpr,
      includePIPEDADpoField: organization.featurePipeda,
      showExtendedAddressFormForOptOut: privacyCenter?.showExtendedAddressFormForOptOut,
      showExtendedAddressFormForRequests: privacyCenter?.showExtendedAddressFormForRequests,
      showPhoneNumberFormForOptOut: privacyCenter?.showPhoneNumberFormForOptOut,
      showPhoneNumberFormForRequests: privacyCenter?.showPhoneNumberFormForRequests,
      requireFirstLastNameOnYpcPage: privacyCenter?.requireFirstLastNameOnYpcPage,
    },
    orgRequest,
    privacyCentersRequest,
    privacyCenter,
    updatePrivacyPolicyRequest,
  };
};

type UsePrivacyCenterSettings = {
  settings: PrivacyCenterSettingsProps;
  orgRequest: NetworkRequestState;
  privacyCentersRequest: NetworkRequestState;
  privacyCenter: PrivacyCenterDto | null;
  updatePrivacyPolicyRequest: NetworkRequestState;
};
